import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Menu, X, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { ApplyDropdown } from './ApplyDropdown';
import { useSiteLogo } from '../../lib/useSiteLogo';

export const HeaderV2 = ({ onApplySelect }) => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const logo = useSiteLogo(theme);

  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'tr' ? 'en' : 'tr');

  const scrollTo = useCallback((e, hash, isMobile = false) => {
    e.preventDefault();
    if (isMobile) setMobileOpen(false);
    setTimeout(() => {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, isMobile ? 280 : 30);
  }, []);

  const nav = [
    { href: '#features', label: t('v2_nav_features') },
    { href: '#integrations', label: t('v2_nav_integrations') },
    { href: '#references', label: t('v2_nav_references') },
    { href: '#contact', label: t('v2_nav_contact') },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/70 backdrop-blur-2xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 min-w-[54px] min-h-[54px]" data-testid="v2-header-logo">
            <img src={logo} alt="AgrosJet" width="94" height="48" loading="eager" decoding="async" fetchpriority="high" className="h-[48px] w-auto object-contain" />
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollTo(e, item.href)}
                className="px-4 py-2 text-foreground/60 hover:text-foreground text-sm font-medium rounded-lg hover:bg-foreground/5 transition-all"
                data-testid={`v2-nav-${item.href.slice(1)}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={toggleLanguage}
              className="text-foreground/60 hover:text-foreground hover:bg-foreground/5 flex items-center gap-1.5 px-2"
              data-testid="v2-language-toggle">
              <Globe className="w-4 h-4" />
              <span className="text-xs font-semibold">{i18n.language === 'tr' ? 'EN' : 'TR'}</span>
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme}
              className="text-foreground/60 hover:text-foreground hover:bg-foreground/5 flex"
              data-testid="v2-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            <div className="hidden sm:block">
              <ApplyDropdown onSelect={onApplySelect} size="sm" testIdPrefix="v2-header-apply" align="end" />
            </div>

            <Button variant="ghost" size="icon"
              className="md:hidden text-foreground/60 hover:text-foreground hover:bg-foreground/5"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="v2-mobile-menu-btn">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-3 pb-3"
            >
              <nav className="flex flex-col gap-1">
                {nav.map((item) => (
                  <a key={item.href} href={item.href}
                    onClick={(e) => scrollTo(e, item.href, true)}
                    className="px-4 py-3 text-foreground/70 hover:text-foreground hover:bg-foreground/5 rounded-lg transition-colors"
                    data-testid={`v2-mobile-nav-${item.href.slice(1)}`}
                  >
                    {item.label}
                  </a>
                ))}
                <div className="mt-3 pt-3 border-t border-border">
                  <ApplyDropdown
                    onSelect={(v) => { setMobileOpen(false); onApplySelect(v); }}
                    size="md"
                    testIdPrefix="v2-mobile-apply"
                  />
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
